import {Value} from "patient-ping-remedy/packages/dropdown-list/lib/DropdownList";
import {EncounterType} from "@/api/dto/client-profile";
import {
  AppRole,
  DataRole,
  EffectivePermissions,
  FeatureRole
} from "@/api/dto/permissions";

export enum GroupType {
  SNF = 'SNF',
  HHA = 'HHA',
  LTAC = 'LTAC',
  REHAB_HOS = 'REHAB_HOS',
  HOSPICE = 'HOSPICE',
  ALF = 'ALF',
  HL7_BASED_PROGRAM = 'HL7_BASED_PROGRAM',
  CCRC = 'CCRC',
  ADH = 'ADH',
  URGENT_CARE = 'URGENT_CARE',
  PALLIATIVE = 'PALLIATIVE',
  CRISIS = 'CRISIS',
  HOS = 'HOS',
  OWNING_ORG = 'OWNING_ORG',
  PRIVATE_PROGRAM = 'PRIVATE_PROGRAM',
}

export enum FeatureToggle {
  ANALYTICS_TAB = 'ANALYTICS_TAB',
  CARECO_3415_NEW_USER_PERMISSIONS = 'CARECO_3415_NEW_USER_PERMISSIONS',
  CARECO_3810_AWS_COMMUNICATIONS = 'CARECO_3810_AWS_COMMUNICATIONS',
  CARECO_3833_REACT_HOOK_FORM_REFACTOR = 'CARECO_3833_REACT_HOOK_FORM_REFACTOR',
  CARECO_5541_CARE_JOURNEY_PRE_REVIEW = 'CARECO_5541_CARE_JOURNEY_PRE_REVIEW',
}

export enum AppSettingDataType {
  BOOLEAN = 'boolean',
  INTEGER = 'integer',
  STRING = 'string',
}

export type RiskFlags = {
  readmissionRiskEndDate: string,
  recentInpatientStayEndDate: string,
  highUtilizerEndDate: string,
}

export type AppSetting = {
  name: string,
  value: string,
  dataType: AppSettingDataType,
  title: string,
  description: string,
}

export interface FeatureToggleFlag {
  name: FeatureToggle;
  enabled: boolean;
  strategyId: FeatureToggleStrategy;
  parameters: Record<string, string | string[]>;
}


export enum FeatureToggleStrategy {
  //assumed as group id in toggles console
  USERNAME = 'username',
  DEFAULT = 'default'
}

export interface FeatureToggleResponse {
  flags: FeatureToggleFlag[];
}

export type Attestation = {
  document_version: string;
  text: string;
}

export type LoginResponse = {
  attestation: Attestation;
  phone_verified: boolean;
}

export enum LoginErrorType {
  SSO_ACCESS_DENIED = 'SSO_ACCESS_DENIED',
  USER_DELETED = 'USER_DELETED',
  USER_DISABLED = 'USER_DISABLED',
}

export type LoginError = {
  title: string;
  type: LoginErrorType;
  message?: string;
}

export type SurveyJSLicenseKey = {
  licenseKey: string;
}

export type BackendVersions = {
  carecoWebapp: BackendVersion;
  carecoEvents: BackendVersion;
  thoth: BackendVersion;
  clientWorkflowEngine: BackendVersion;
}
export type BackendVersion = {
  GIT_BRANCH: string;
  GIT_COMMIT: string;
  dirty?: boolean;
}

export interface AuthoritiesResponse {
  role: string,
  privileges: string[]
}

export interface RosterResponse {
  data: Rosters,
  dataRoleOptions: Value[],
  featureRoleOptions: Value[],
  rosterOptions: Value[],
  organizationOptions: Value[]
}

export interface Rosters {
  data: Array<Roster>,
  count: number
  data_roles: Array<DataRole>,
  feature_roles: Array<FeatureRole>,
}

export interface Roster {
  id: number,
  name: string,
  npi: string,
  type: string,
  typeDisplayName: string,
  active: boolean,
  authorized: boolean,
  admittingFacility: boolean,
  automated: boolean,
  address: Address,
  organization: {
    id: string,
    name: string
  }
  uuid?: string,
  isPart2Facility?: boolean,
  configuration: GroupConfiguration,
  permissions: RosterPermissions,
  permissionsNew: RosterPermissionsNew
  units?: Unit[],
}

export interface BasicRosterResponse {
  id: number,
  name: string,
  type: string,
}

export interface CareNoteTrigger {
  taskType: string | null,
  attestationRequired: boolean,
  step: string | null,
  stepStatus: string | null,
}

export interface AcknowledgementTrigger {
  journeyType: string | null,
  recipient: string | null,
  channel: string | null,
  secureEmailTemplateId: number | null
}

export enum AcknowledgementRecipientOptions {
  PCP = 'PCP',
  CARE_TEAM = 'Care team'
}

export enum AcknowledgementChannelOptions {
  FAX = 'Fax',
}

export interface OutreachSummaryTrigger {
  taskType: string | null,
  delay: string | null,
}

export interface GroupConfiguration {
  contractedProvider: boolean,
  crfRequiredDate: string,
  outreachAttemptsSummarySftpEnabled: boolean,
  careNoteSftpEnabled?: boolean,
  manualCareTeamReferralEnabled: boolean,
  notifyCareTeamEnabled: boolean,
  careNoteTriggers?: CareNoteTrigger[],
  outreachSummaryTriggers?: OutreachSummaryTrigger[],
  manualSendCareNoteAllowedTaskTypes?: string[],
  acknowledgementTriggers?: AcknowledgementTrigger[],
  careTeamPhoneNumber?: string,
}

export interface UpdateGroupConfigurationRequest {
  configuration: GroupConfiguration,
}

export interface PdmpCredentials {
  username: string | null;
  has_credentials: boolean;
}

export interface UpdatePdmpCredentialsRequest {
  username: string;
  password: string;
}

export interface Address {
  address1: string,
  address2?: string,
  city: string,
  state: string,
  zip: string,
  county: string,
  timeZone: string
}

export interface Clients {
  page: number,
  pageSize: number,
  searchTotal: number,
  rosterTotal: number,
  rosterClientsTotal: number,
  clients: Array<Client>
}

export interface Client {
  patientId: string,
  firstName: string,
  middleName: string,
  lastName: string,
  dateOfBirth: string,
  gender?: string,
  userAssignments: ClientUserAssignment[],
  lastEventDate: string,
  lastEventType: EncounterType,
  lastEventStatus: string,
  lastEventGroupType?: string,
  riskFlags: RiskFlags,
  clientId: string
}

export interface IncompleteAdmissionCRFsResponse {
  totalIncompleteAdmissionCount: number;
  incompleteAdmissionCrfs: IncompleteAdmissionCRF[];
}

export interface IncompleteAdmissionCRF {
    clientName: string,
    patientId: string,
    rosterId: number,
    formId: number,
    clientId: string,
    clientGroupAdmission: string,
    clientAdmissionDate: string,
    formCreated: boolean,
    encounterId: string,
    client: Client,
    crfCompleted: boolean,
    contractedProvider: boolean,
}

export interface IncompleteDischargeCRFsResponse {
  totalIncompleteAdmissionAndDischargeCount: number;
  approaching90DaysSinceDischargeCount: number;
  incompleteAdmissionAndDischargeCrfs: IncompleteDischargeCRF[];
}

export interface IncompleteDischargeCRF {
    clientName: string,
    patientId: string,
    rosterId: number,
    formId: number,
    clientId: string,
    clientGroup: string,
    clientDischargeDate: string,
    clientAdmissionForm: boolean,
    clientDischargeForm: boolean,
    isApproaching90DaysSinceDischarge: boolean,
    formCreated: boolean,
    encounterId: string,
    client: Client,
}

export interface ClientUserAssignment {
  user: User,
}

export interface Organization {
  createdAt: string,
  id: number,
  name: string,
  active: boolean,
  groups: OrganizationGroups[],
  configuration: OrganizationConfiguration,
}

export interface OrganizationConfiguration {
  clientDropOffDays: number,
  restrictClientProfileViewForContractedProvider: boolean,
  hideNewEventsWithoutActiveAdmission: boolean,
  allowOverseasCareNavigatorAccess: boolean,
}

export interface UpdateOrganizationConfiguration {
  configuration: OrganizationConfiguration,
}

export interface UserRoster {
  roster: Roster, // not the full roster
  data_role: DataRole,
  feature_role: FeatureRole
  units?: {
    unitId: string,
    id: number,
  }[],
}

export interface Users {
  searchTotal: number,
  rosterTotal: number,
  users: Array<User>
}

export interface User {
  id: string,
  username: string,
  rosters: UserRoster[],
  organizations: string[],
  effectivePermissions: EffectivePermissions,
  role: AppRole,
  email: string,
  phone: string,
  title: string,
  image: string;
  first_name: string,
  last_name: string,
  created_at: string,
  updated_at: string,
  last_login: string,
  permissions_last_updated_at: string,
  activation_email_scheduled_at?: string,
  status: string,
  enabled: boolean,
  dea_number: string,
  npi_number: string,
  sln_value: string,
  sln_type: string,
  sln_jurisdiction: string,
  pdmp_role: string
  only_show_unread_notifications: boolean,
  only_show_unread_messages: boolean,
  only_assigned_client_notifications: boolean,
  only_assigned_task_notifications: boolean,
  sso_connection?: string
  is_sso: boolean,
  roundtrip_sso?: boolean,
  deleted?: boolean
}

export interface BasicUser {
  id: string,
  email: string,
  first_name: string,
  last_name: string,
  deleted: boolean
}

export interface RosterSelection {
  roster_id: number,
  data_role_id: number | null, // these are null for SUPER_ADMIN
  feature_role_id: number | null // these are null for SUPER_ADMIN
}

export interface UserRequest {
  email: string,
  first_name: string,
  last_name: string,
  phone: string,
  title: string,
  rosters: RosterSelection[],
  enabled: boolean,
  pdmp_role?: string,
  is_sso: boolean,
  roundtrip_sso?: boolean,
}

export interface UserPatchRequest {
  only_show_unread_notifications?: boolean,
  only_show_unread_messages?: boolean,
  only_assigned_client_notifications?: boolean,
  only_assigned_task_notifications?: boolean,
}

export interface UserProfileRequest {
  email: string,
  first_name: string,
  last_name: string,
  phone: string,
  title: string,
  dea_number: string,
  npi_number: string,
  sln_value: string,
  sln_type: string,
  sln_jurisdiction: string,
  pdmp_role: string
}

export interface OrganizationGroups {
  active: boolean,
  groupName: string,
}

export interface RosterPermissions {
  adtEnabled: boolean,
  obReferralsEnabled: boolean,
  obCrisisEnabled: boolean,
  findHelpEnabled: boolean,
  pdmpEnabled: boolean,
  jiEnabled: boolean,
  roundtripEnabled: boolean,
  clientListEnabled: boolean,
  clientProfileEnabled: boolean,
  formsEnabled: boolean,
  userPermissionsEnabled: boolean,
  userReportingEnabled: boolean,
  stateReportingEnabled: boolean,
  logEnabled: boolean,
  notificationsEnabled: boolean,
  tasksEnabled: boolean,
  networkRegistryEnabled: boolean,
  referralsEnabled: boolean,
}

export interface RosterPermissionsNew {
  dataPermissions: DataPermissions;
  featurePermissions: FeaturePermissions;
}

export interface DataPermissions {
  adtEnabled: boolean;
  obReferralsEnabled: boolean;
  obCrisisEnabled: boolean;
  findHelpEnabled: boolean;
  pdmpEnabled: boolean;
  jiEnabled: boolean;
  roundtripEnabled: boolean;
}

export interface FeaturePermissions {
  clientListEnabled: boolean;
  formsEnabled: boolean;
  adminManagementEnabled: boolean;
  analyticsEnabled: boolean;
  tasksEnabled: boolean;
  myNetworkEnabled: boolean;
  networkRegistryBrowserEnabled: boolean;
  networkFacilitiesAndUnitsEnabled: boolean;
  referralsEnabled: boolean;
}

export interface ClientGroup {
  firstName: string,
  lastName: string,
  admittingFacilityId?: number,
}

export interface WalkMeData {
  createdAt: number,
  createdAtUnix: number,
  email: string,
  firstLogin: number,
  firstLoginUnix: number,
  firstName: string,
  lastName: string,
  name: string,
  groupIds: string,
  groupNames: string,
  groupStates: string,
  groupTypes: string,
  dataRoles: string,
  featureRoles: string,
  isAdmittingFacility: string,
  isAutomated: string,
  lastLogin: number,
  lastLoginUnix: number,
  orgId: string,
  orgName: string,
  websessions: number,
  orgCreated?: number,
  type?: string,
}

export interface AdminDetail {
  role: string;
  fullName: string;
  email: string;
}

export interface AdminDetailByRoster {
  rosterId: number,
  rosterName: string,
  adminDetails: AdminDetail[]
}

export interface EventConsumer {
  id?: Nullable<number>;
  name: string;
  consumerUuid?: string;
  filters: { [key: string]: any };
  createdAt?: string;
  updatedAt?: string;
}

export interface DiagnosisCodeResponse {
  id: number;
  code: string;
  full_code: string;
  parent_code: string;
  synonym: string;
}

export enum DropdownItemType {
  DE_TREATMENT_UNIT = 'DE_TREATMENT_UNIT',
  TASK_STEP_NAME = 'TASK_STEP_NAME',
  DE_REFERRAL_AGENCY = 'DE_REFERRAL_AGENCY',
}

export interface DropdownItemResponse {
  id?: number;
  type: DropdownItemType;
  value: string;
  label: string;
  active: boolean;
}

export interface Permission {
  permission: string,
  level: string | null,
}

export interface RosterSelectionNew {
  rosterId: number,
  availableUnits?: string[],
  dataPermissions?: Permission[],
  featurePermissions?: Permission[],
}

export interface UserRequestNew {
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
  title: string,
  rosters: RosterSelectionNew[],
  ssoUser: boolean,
  roundTripSso?: boolean,
  activationEmailSchedule?: string
  pdmpRole?: string,
  enabled?: boolean,
}

export interface Unit {
  id: string,
  name: string,
  description?: string,
  operational_beds?: number,
}

export interface Facility {
  id: string,
  name: string,
  units: Unit[],
}

export interface RosterFacility {
  rosterId: number,
  facility: Facility,
}

export interface BatchFacilitiesResponse {
  facilities: RosterFacility[],
}

export interface UserAccessTemplateResponse  {
  id: number,
  templateName: string,
  displayName: string,
  description?: string,
  permissions: {
    permission: string,
    level: string,
  }[]
}

export interface DmcsGenerateDocumentResponseContext {
  resolvedTitle: string,
  document: {
    DOC_ID: string,
    PDF_DOC: string
  }
}

export interface ReferralAttachmentResponse {
  base64Content: string,
  contentType: string,
  byteLength: string
}

export interface SecureEmailTemplate {
  template_id: string,
  name: string,
  published_version: number,
  published_at: string
}
